import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  ChevronDown,
  ChevronUp,
  Check,
  Search,
  Plus,
  // CircleX
} from "lucide-react";
import { useGetAllCompanyQuery } from "../../../_features/company/api/companyApi";
import { openModal } from "../../../_app/entityModals/store";
import "./_company-select.scss";
import { skipToken } from "@reduxjs/toolkit/query";

const CompanySelect = ({
  label = "Company",
  value = null,
  onChange = () => {},
  context = "invoice",
  disabled = false,
  error = null,
  className = "",
  style = {},
}) => {
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [hasMoreItems, setHasMoreItems] = useState(true);
  const [companiesArray, setCompaniesArray] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  const [isLoadingMore, setIsLoadingMore] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [isSearching, setIsSearching] = useState(false);

  // API call
  const { data: allCompanies, isFetching } = useGetAllCompanyQuery(
    searchQuery !== null
      ? {
          context,
          page,
          limit: 10,
          filters: JSON.stringify(
            searchQuery
              ? { sort: "-createdAt", search: searchQuery }
              : { sort: "-createdAt" }
          ),
        }
      : skipToken
  );

  // Initialize selectedItem when value prop changes - searchInput is now
  // purely the panel's own search box state, not tied to the selection.
  useEffect(() => {
    setSelectedItem(value || null);
  }, [value]);

  // Reset page + results whenever searchQuery changes
  useEffect(() => {
    setPage(0);
    setCompaniesArray([]);
    if (searchQuery !== "") setIsSearching(true);
  }, [searchQuery]);

  // Process API response
  useEffect(() => {
    if (allCompanies?.companies) {
      const companiesData = allCompanies.companies
        .filter((c) => c?.isIndividual || c?.companyName?.trim()?.length > 0)
        .map((c) => ({
          ...c,
          name: c.isIndividual ? c.individualName : `${c.companyName}`,
        }));

      if (page === 0) {
        setCompaniesArray(companiesData);
      } else {
        setCompaniesArray((prev) => {
          const newOnes = companiesData.filter(
            (n) => !prev.some((p) => p._id === n._id)
          );
          return [...prev, ...newOnes];
        });
      }

      setHasMoreItems(allCompanies.companies.length === 10);
      setIsSearching(false);
    }
  }, [allCompanies, page]);

  // Debounce search input → query
  const searchTimeoutRef = useRef(null);
  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setSearchQuery(searchInput.trim());
    }, 300); // Reduced from 500ms for better responsiveness
    return () => clearTimeout(searchTimeoutRef.current);
  }, [searchInput]);

  // Infinite scroll observer
  const observerRef = useRef(null);
  const lastItemRef = useCallback(
    (node) => {
      if (observerRef.current) observerRef.current.disconnect();
      if (node && hasMoreItems && !isLoadingMore && !isFetching) {
        observerRef.current = new IntersectionObserver((entries) => {
          if (entries[0].isIntersecting) {
            setIsLoadingMore(true);
            setPage((p) => p + 1);
            setTimeout(() => setIsLoadingMore(false), 1000);
          }
        });
        observerRef.current.observe(node);
      }
    },
    [hasMoreItems, isLoadingMore, isFetching]
  );

  // Handlers
  // The dropdown is portaled (see the render below) with a JS-computed
  // fixed position, not CSS `position: absolute` inside this component's
  // own container - it previously overlapped whatever came right after it
  // in the page (e.g. a sibling field) instead of pushing content down or
  // avoiding it, since absolute positioning doesn't affect sibling layout.
  const openDropdown = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPosition({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    }
    // Search always starts fresh when the panel opens - it's a search box
    // inside the panel now, not the trigger itself, so there's no reason
    // for it to carry over the previous session's text.
    setSearchInput("");
    setSearchQuery("");
    setPage(0);
    setHighlightedIndex(-1);
    setIsOpen(true);
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    setSearchInput(val);
    setSearchQuery(val);
    setHighlightedIndex(-1);
  };

  const handleSelectItem = (item) => {
    setSelectedItem(item);
    setIsOpen(false);
    setHighlightedIndex(-1);
    onChange(item);
  };

  // Opens the shared Create company panel (see _app/entityModals) instead
  // of forcing the user to abandon whatever form this select lives in
  // (Proposal, Subscription, Invoice, Upsell/Downsell) just to go create
  // the missing company elsewhere and come back. Resolves to the created
  // company - or null if the panel was closed without submitting - so
  // there's nothing to select in that case.
  const handleCreateNew = async () => {
    // Closes the dropdown first - it's a z-overlay-nested portal (needs to
    // sit above a parent ModalShell this select might already live inside,
    // see the .company-select-dropdown stylesheet comment), which would
    // otherwise render above the Create company panel's own z-overlay
    // ModalShell and block clicks into it, exactly the bug that comment
    // describes for the reverse ordering.
    setIsOpen(false);
    const created = await openModal("create_company");
    if (!created) return;
    handleSelectItem({
      ...created,
      name: created.isIndividual ? created.individualName : created.companyName,
    });
  };

  //   const handleClear = (e) => {
  //     e.stopPropagation();
  //     setSelectedItem(null);
  //     setSearchInput("");
  //     setSearchQuery("");
  //     onChange(null);
  //     setIsOpen(false);
  //   };

  // Outside click - checks both the trigger's own container AND the
  // portaled dropdown, since the dropdown no longer lives inside the
  // container in the DOM.
  const containerRef = useRef(null);
  useEffect(() => {
    const handler = (e) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target) &&
        !(dropdownRef.current && dropdownRef.current.contains(e.target))
      ) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Keep the dropdown on-screen once open, and re-close on scroll/resize -
  // a fixed-position portal doesn't move with its trigger. Mirrors
  // _components/ui/Select's identical logic.
  useEffect(() => {
    if (!isOpen) return;
    if (dropdownRef.current && triggerRef.current) {
      const dropdownRect = dropdownRef.current.getBoundingClientRect();
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - triggerRect.bottom;
      const spaceAbove = triggerRect.top;

      setPosition((prev) => {
        let top = prev.top;
        if (dropdownRect.height > spaceBelow && spaceAbove > spaceBelow) {
          top = triggerRect.top - dropdownRect.height - 4;
        } else if (dropdownRect.bottom > window.innerHeight) {
          top = window.innerHeight - dropdownRect.height - 10;
        }
        return {
          ...prev,
          top,
          left: dropdownRect.right > window.innerWidth ? window.innerWidth - dropdownRect.width - 10 : prev.left,
        };
      });
    }
    // Resize events target `window` itself, not a Node - `contains()` would
    // throw on it, so resize always closes unconditionally. Scroll keeps the
    // containment check (scrolling inside the dropdown shouldn't close it).
    const closeOnScroll = (event) => {
      if (
        dropdownRef.current &&
        event.target instanceof Node &&
        dropdownRef.current.contains(event.target)
      )
        return;
      setIsOpen(false);
    };
    const closeOnResize = () => setIsOpen(false);
    window.addEventListener("scroll", closeOnScroll, true);
    window.addEventListener("resize", closeOnResize);
    return () => {
      window.removeEventListener("scroll", closeOnScroll, true);
      window.removeEventListener("resize", closeOnResize);
    };
  }, [isOpen]);

  // Autofocus the search box every time the panel opens, mirroring
  // ui/Select - typing to filter works immediately without an extra click.
  useEffect(() => {
    if (isOpen) searchInputRef.current?.focus();
  }, [isOpen]);

  // Render dropdown item
  const renderItem = (item, index) => {
    const isHighlighted = index === highlightedIndex;
    const isSelected = selectedItem && selectedItem._id === item._id;
    const isLast = index === companiesArray.length - 1;
    return (
      <div
        key={item._id || index}
        ref={isLast ? lastItemRef : null}
        className={`company-select-item ${isHighlighted ? "highlighted" : ""} ${
          isSelected ? "selected" : ""
        }`}
        onClick={() => handleSelectItem(item)}
        onMouseEnter={() => setHighlightedIndex(index)}
      >
        <div className="item-content">
          <span className="item-name">{item.name}</span>
        </div>
        {isSelected && <Check size={16} className="check-icon" />}
      </div>
    );
  };

  const portalRoot = typeof document !== "undefined" && document.getElementById("table-action-root");

  return (
    <div
      ref={containerRef}
      className={`company-select-container ${className} ${
        disabled ? "disabled" : ""
      } ${error ? "error" : ""}`}
      style={style}
    >
      {label && <label className="company-select-label">{label}</label>}
      <div
        ref={triggerRef}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        className={`company-select-input ${isOpen ? "open" : ""} ${disabled ? "disabled" : ""} ${
          error ? "error" : ""
        }`}
        onClick={() => {
          if (!disabled) openDropdown();
        }}
        onKeyDown={(e) => {
          if (disabled) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            openDropdown();
          }
        }}
      >
        {selectedItem ? (
          <span className="company-select-value">{selectedItem.name}</span>
        ) : (
          <span className="company-select-placeholder">Select company</span>
        )}
        <div className="input-actions">
          {/* {selectedItem && !disabled && (
            <button
              type="button"
              className="clear-button"
              onClick={handleClear}
            >
              <CircleX size={16} />
            </button>
          )} */}
          <div className="dropdown-arrow">
            {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </div>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {isOpen &&
        portalRoot &&
        createPortal(
          <div
            ref={dropdownRef}
            className="company-select-dropdown"
            style={{ position: "fixed", top: position.top, left: position.left, width: position.width }}
          >
            <div className="company-select-search">
              <input
                ref={searchInputRef}
                type="text"
                value={searchInput}
                placeholder="Search companies..."
                onChange={handleInputChange}
                className="company-select-search-input"
              />
            </div>
            <div className="company-select-options">
              {isFetching && companiesArray.length === 0 ? (
                <div className="loading-state">
                  <div className="company-spinner company-spinner-purple"></div>
                  <span>Loading companies...</span>
                </div>
              ) : companiesArray.length > 0 ? (
                <>
                  {companiesArray.map((item, i) => renderItem(item, i))}
                  {isLoadingMore && (
                    <div className="loading-more">
                      <div className="company-spinner company-spinner-purple"></div>
                      <span>Loading more...</span>
                    </div>
                  )}
                </>
              ) : searchInput.trim() !== "" ? (
                <div className="no-results">
                  <Search size={16} />
                  <span>No companies found for "{searchInput}"</span>
                </div>
              ) : (
                <div className="empty-state">
                  <Search size={16} />
                  <span>Start typing to search companies</span>
                </div>
              )}
            </div>
            <button
              type="button"
              className="company-select-create-new"
              onClick={handleCreateNew}
            >
              <Plus size={14} />
              <span>Create new company</span>
            </button>
          </div>,
          portalRoot
        )}
    </div>
  );
};

export default CompanySelect;
